import React, { useState } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from './ui/button';

/**
 * QuickFlip — One-Time Onboarding
 * 
 * FINAL COPY (locked):
 * Card 1: "People sharing real moments in time."
 * Card 2: "Tap a day. See what people did."
 * Card 3: "Add your own when you're ready."
 * 
 * Rules:
 * - 3 static cards only
 * - Uses exact LikeKind logo characters
 * - Light, fun, adventurous tone
 * - NOT abstract, NOT instructional
 * - After dismissal → immediate drop into main browse timeline
 */

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png";

const CARDS = [
  {
    id: 1,
    copy: 'Moments shared between friends.',
  },
  {
    id: 2,
    copy: 'Tap a day. See what friends were up to.',
  },
  {
    id: 3,
    copy: 'Add your own when you\'re ready.',
  }
];

export default function QuickFlip({ onComplete }) {
  const [currentCard, setCurrentCard] = useState(0);

  const handleNext = () => {
    if (currentCard < CARDS.length - 1) {
      setCurrentCard(currentCard + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentCard > 0) {
      setCurrentCard(currentCard - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const card = CARDS[currentCard];
  const isLastCard = currentCard === CARDS.length - 1;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="rounded-2xl p-6 mb-6" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          {/* LikeKind Logo Characters - exact logo in scene */}
          <div className="relative w-full h-48 rounded-xl overflow-hidden flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
            <img 
              src={LOGO_URL}
              alt="LikeKind" 
              className="w-32 h-32 object-contain"
            />
          </div>

          {/* Copy - light, one line */}
          <div className="mt-6 text-center">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
              {card.copy}
            </h2>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {CARDS.map((_, idx) => (
            <div 
              key={idx}
              className="w-2 h-2 rounded-full transition-all"
              style={{ 
                backgroundColor: idx === currentCard ? 'var(--fy-accent-positive)' : 'var(--fy-border-subtle)',
                transform: idx === currentCard ? 'scale(1.2)' : 'scale(1)'
              }}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          {/* Back button */}
          <Button
            onClick={handlePrev}
            variant="ghost"
            disabled={currentCard === 0}
            className="w-10 h-10"
            style={{ color: currentCard === 0 ? 'var(--fy-text-disabled)' : 'var(--fy-text-secondary)' }}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>

          {/* Main action */}
          {isLastCard ? (
            <Button
              onClick={onComplete}
              className="flex-1 mx-4 h-12 rounded-full font-bold"
              style={{ backgroundColor: 'var(--fy-accent-positive)', color: 'var(--fy-background-primary)' }}
            >
              Start wandering
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="flex-1 mx-4 h-12 rounded-full font-bold"
              style={{ backgroundColor: 'var(--fy-accent-positive)', color: 'var(--fy-background-primary)' }}
            >
              Next
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          )}

          {/* Skip button */}
          <Button
            onClick={handleSkip}
            variant="ghost"
            className="text-sm"
            style={{ color: 'var(--fy-text-disabled)' }}
          >
            Skip
          </Button>
        </div>
      </div>
    </div>
  );
}

// Export for Settings access
export { CARDS as ONBOARDING_CARDS };
